import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Phone, RefreshCw, Search, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "healbay_admin_token";
const LOGO_URL = "/logo-new-cropped.png";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];

const initialCredentials = {
  email: "",
  password: "",
};

const initialPasswordForm = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const statusClassMap = {
  new: "bg-cyan-50 text-cyan-800 border-cyan-200",
  contacted: "bg-indigo-50 text-indigo-800 border-indigo-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  closed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString();
};

const statusLabel = (value) => STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;

const AdminDashboard = () => {
  const [credentials, setCredentials] = useState(initialCredentials);
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [summary, setSummary] = useState({
    total_inquiries: 0,
    new: 0,
    contacted: 0,
    in_progress: 0,
    closed: 0,
    received_today: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    country: "",
  });
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState(initialPasswordForm);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);

  useEffect(() => {
    document.documentElement.lang = "en";
    document.documentElement.dir = "ltr";
    document.title = "HealBay Connect Admin Dashboard";
  }, []);

  const fetchDashboardData = async (adminToken) => {
    setIsLoadingData(true);
    try {
      const headers = {
        Authorization: `Bearer ${adminToken}`,
      };

      const [summaryResponse, inquiriesResponse] = await Promise.all([
        axios.get(`${API}/admin/summary`, { headers }),
        axios.get(`${API}/admin/inquiries`, { headers }),
      ]);

      setSummary(summaryResponse.data);
      setInquiries(inquiriesResponse.data);
    } catch (error) {
      const errorStatus = error?.response?.status;
      if (errorStatus === 401 || errorStatus === 403) {
        toast.error("Admin session expired. Please login again.");
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Unable to load inquiries right now.");
      }
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData(token);
    }
  }, [token]);

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inquiry) => {
      const normalizedSearch = filters.search.trim().toLowerCase();
      const normalizedCountry = filters.country.trim().toLowerCase();

      const statusMatch = filters.status === "all" || inquiry.status === filters.status;
      const countryMatch = !normalizedCountry || inquiry.country.toLowerCase().includes(normalizedCountry);

      const searchableContent = [
        inquiry.full_name,
        inquiry.email,
        inquiry.phone_whatsapp,
        inquiry.country,
        inquiry.treatment_type,
        inquiry.message,
      ]
        .join(" ")
        .toLowerCase();

      const searchMatch = !normalizedSearch || searchableContent.includes(normalizedSearch);
      return statusMatch && countryMatch && searchMatch;
    });
  }, [inquiries, filters]);

  const updateStatus = async (inquiryId, nextStatus) => {
    try {
      const response = await axios.patch(
        `${API}/admin/inquiries/${inquiryId}/status`,
        { status: nextStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const updatedInquiry = response.data;

      setInquiries((previous) =>
        previous.map((item) => (item.inquiry_id === updatedInquiry.inquiry_id ? updatedInquiry : item)),
      );

      setSummary((previous) => {
        const baseline = {
          ...previous,
          new: 0,
          contacted: 0,
          in_progress: 0,
          closed: 0,
        };

        const reconstructed = inquiries
          .map((item) => (item.inquiry_id === updatedInquiry.inquiry_id ? updatedInquiry : item))
          .reduce(
            (accumulator, item) => {
              accumulator[item.status] = (accumulator[item.status] || 0) + 1;
              return accumulator;
            },
            { ...baseline },
          );

        return reconstructed;
      });

      if (selectedInquiry?.inquiry_id === inquiryId) {
        setSelectedInquiry(updatedInquiry);
      }

      toast.success("Inquiry status updated.");
    } catch (error) {
      toast.error("Could not update inquiry status.");
    }
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setIsLoggingIn(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const accessToken = response.data.access_token;
      localStorage.setItem(ADMIN_TOKEN_KEY, accessToken);
      setToken(accessToken);
      setCredentials(initialCredentials);
      toast.success("Admin login successful.");
    } catch (error) {
      toast.error("Invalid admin credentials.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
    setInquiries([]);
    setSelectedInquiry(null);
    setIsDetailsOpen(false);
    toast.success("Logged out successfully.");
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New password and confirmation do not match.");
      return;
    }

    setIsChangingPassword(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Password changed successfully.");
      setPasswordForm(initialPasswordForm);
      setIsPasswordSectionOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to change password.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-8" data-testid="admin-login-page">
        <Toaster position="top-right" richColors />
        <div className="mx-auto flex min-h-[85vh] w-full max-w-xl items-center justify-center">
          <Card className="w-full border border-slate-200 bg-white shadow-sm" data-testid="admin-login-card">
            <CardContent className="space-y-6 p-7 md:p-9">
              <div className="space-y-4 text-center" data-testid="admin-login-header">
                <img
                  src={LOGO_URL}
                  alt="HealBay Connect logo"
                  className="mx-auto h-20 w-auto max-w-[220px] rounded-md object-contain object-center"
                  data-testid="admin-login-logo"
                />
                <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="admin-login-title">
                  Admin Dashboard Login
                </h1>
                <p className="text-sm text-slate-600" data-testid="admin-login-subtitle">
                  Sign in to view and manage patient inquiries.
                </p>
              </div>

              <form className="space-y-4" onSubmit={handleLoginSubmit} data-testid="admin-login-form">
                <div className="space-y-2" data-testid="admin-email-field-wrap">
                  <label className="text-sm font-medium text-slate-700" htmlFor="admin-email" data-testid="admin-email-label">
                    Email
                  </label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={credentials.email}
                    onChange={(event) => setCredentials((previous) => ({ ...previous, email: event.target.value }))}
                    required
                    data-testid="admin-email-input"
                  />
                </div>

                <div className="space-y-2" data-testid="admin-password-field-wrap">
                  <label
                    className="text-sm font-medium text-slate-700"
                    htmlFor="admin-password"
                    data-testid="admin-password-label"
                  >
                    Password
                  </label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={credentials.password}
                    onChange={(event) => setCredentials((previous) => ({ ...previous, password: event.target.value }))}
                    required
                    data-testid="admin-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full rounded-full bg-cyan-600 py-6 text-white hover:bg-cyan-700"
                  data-testid="admin-login-submit-button"
                >
                  {isLoggingIn ? "Signing in..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <Toaster position="top-right" richColors />

      <header className="border-b border-slate-200 bg-white" data-testid="admin-dashboard-header">
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4 md:px-8">
          <div className="flex items-center gap-3" data-testid="admin-branding-wrap">
            <img
              src={LOGO_URL}
              alt="HealBay Connect logo"
              className="h-12 w-auto max-w-[160px] rounded-md object-contain object-left"
              data-testid="admin-header-logo"
            />
            <div className="space-y-0.5" data-testid="admin-header-title-wrap">
              <h1 className="font-heading text-xl font-bold text-slate-900" data-testid="admin-header-title">
                HealBay Connect Admin
              </h1>
              <p className="text-xs text-slate-500" data-testid="admin-header-subtitle">
                Inquiry Operations Dashboard
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="admin-header-actions">
            <Button
              type="button"
              onClick={() => fetchDashboardData(token)}
              className="rounded-full bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingData ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              type="button"
              onClick={handleLogout}
              className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
              data-testid="admin-logout-button"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl space-y-6 px-6 py-8 md:px-8" data-testid="admin-main-content">
        <section className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="admin-summary-grid">
          {[
            { label: "Total", value: summary.total_inquiries, key: "total" },
            { label: "New", value: summary.new, key: "new" },
            { label: "Contacted", value: summary.contacted, key: "contacted" },
            { label: "In Progress", value: summary.in_progress, key: "in_progress" },
            { label: "Closed", value: summary.closed, key: "closed" },
            { label: "Today", value: summary.received_today, key: "today" },
          ].map((item) => (
            <Card key={item.key} className="border border-slate-200 bg-white" data-testid={`admin-summary-card-${item.key}`}>
              <CardContent className="space-y-2 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid={`admin-summary-label-${item.key}`}>
                  {item.label}
                </p>
                <p className="font-heading text-2xl font-bold text-slate-900" data-testid={`admin-summary-value-${item.key}`}>
                  {item.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </section>

        <Card className="border border-slate-200 bg-white" data-testid="admin-filters-card">
          <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
            <div className="relative" data-testid="admin-search-wrap">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input
                value={filters.search}
                onChange={(event) => setFilters((previous) => ({ ...previous, search: event.target.value }))}
                className="pl-9"
                placeholder="Search by name, treatment, phone..."
                data-testid="admin-search-input"
              />
            </div>

            <select
              value={filters.status}
              onChange={(event) => setFilters((previous) => ({ ...previous, status: event.target.value }))}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="admin-status-filter-select"
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map((statusItem) => (
                <option key={statusItem.value} value={statusItem.value}>
                  {statusItem.label}
                </option>
              ))}
            </select>

            <Input
              value={filters.country}
              onChange={(event) => setFilters((previous) => ({ ...previous, country: event.target.value }))}
              placeholder="Filter by country"
              data-testid="admin-country-filter-input"
            />
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white" data-testid="admin-change-password-card">
          <CardContent className="space-y-4 p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3" data-testid="admin-change-password-header">
              <div data-testid="admin-change-password-header-copy">
                <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="admin-change-password-title">
                  Change Dashboard Password
                </h2>
                <p className="text-sm text-slate-600" data-testid="admin-change-password-subtitle">
                  Update your admin password anytime.
                </p>
              </div>

              <Button
                type="button"
                onClick={() => setIsPasswordSectionOpen((previous) => !previous)}
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
                data-testid="admin-toggle-password-form-button"
              >
                {isPasswordSectionOpen ? "Hide" : "Change Password"}
              </Button>
            </div>

            {isPasswordSectionOpen && (
              <form
                className="grid grid-cols-1 gap-3 md:grid-cols-3"
                onSubmit={handlePasswordChange}
                data-testid="admin-change-password-form"
              >
                <Input
                  type="password"
                  placeholder="Current password"
                  value={passwordForm.current_password}
                  onChange={(event) =>
                    setPasswordForm((previous) => ({ ...previous, current_password: event.target.value }))
                  }
                  required
                  data-testid="admin-current-password-input"
                />
                <Input
                  type="password"
                  placeholder="New password"
                  value={passwordForm.new_password}
                  onChange={(event) => setPasswordForm((previous) => ({ ...previous, new_password: event.target.value }))}
                  required
                  minLength={8}
                  data-testid="admin-new-password-input"
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={passwordForm.confirm_password}
                  onChange={(event) =>
                    setPasswordForm((previous) => ({ ...previous, confirm_password: event.target.value }))
                  }
                  required
                  minLength={8}
                  data-testid="admin-confirm-password-input"
                />
                <div className="md:col-span-3" data-testid="admin-change-password-submit-wrap">
                  <Button
                    type="submit"
                    disabled={isChangingPassword}
                    className="rounded-full bg-cyan-600 text-white hover:bg-cyan-700"
                    data-testid="admin-change-password-submit-button"
                  >
                    {isChangingPassword ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 bg-white" data-testid="admin-inquiries-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto" data-testid="admin-inquiries-table-wrap">
              <table className="w-full min-w-[980px]" data-testid="admin-inquiries-table">
                <thead className="bg-slate-50" data-testid="admin-inquiries-table-head">
                  <tr>
                    {[
                      "Patient",
                      "Country",
                      "Treatment",
                      "Phone",
                      "Submitted",
                      "Status",
                      "Actions",
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                        data-testid={`admin-table-head-${heading.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="admin-inquiries-table-body">
                  {filteredInquiries.map((inquiry) => (
                    <tr key={inquiry.inquiry_id} className="border-t border-slate-100" data-testid={`admin-inquiry-row-${inquiry.inquiry_id}`}>
                      <td className="px-4 py-3" data-testid={`admin-inquiry-patient-${inquiry.inquiry_id}`}>
                        <p className="text-sm font-semibold text-slate-900">{inquiry.full_name}</p>
                        <p className="text-xs text-slate-500">{inquiry.email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`admin-inquiry-country-${inquiry.inquiry_id}`}>
                        {inquiry.country}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`admin-inquiry-treatment-${inquiry.inquiry_id}`}>
                        {inquiry.treatment_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`admin-inquiry-phone-${inquiry.inquiry_id}`}>
                        {inquiry.phone_whatsapp}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`admin-inquiry-submitted-${inquiry.inquiry_id}`}>
                        {formatDateTime(inquiry.submitted_at)}
                      </td>
                      <td className="px-4 py-3" data-testid={`admin-inquiry-status-cell-${inquiry.inquiry_id}`}>
                        <select
                          value={inquiry.status}
                          onChange={(event) => updateStatus(inquiry.inquiry_id, event.target.value)}
                          className={`h-9 rounded-md border px-3 text-xs font-semibold ${statusClassMap[inquiry.status] || statusClassMap.new}`}
                          data-testid={`admin-status-select-${inquiry.inquiry_id}`}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3" data-testid={`admin-inquiry-actions-${inquiry.inquiry_id}`}>
                        <Button
                          type="button"
                          onClick={() => {
                            setSelectedInquiry(inquiry);
                            setIsDetailsOpen(true);
                          }}
                          className="h-9 rounded-full bg-cyan-600 px-4 text-xs text-white hover:bg-cyan-700"
                          data-testid={`admin-view-details-button-${inquiry.inquiry_id}`}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredInquiries.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-12" data-testid="admin-empty-state">
                  <ShieldCheck className="h-8 w-8 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700" data-testid="admin-empty-state-title">
                    No inquiries found for current filters.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl" data-testid="admin-inquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-inquiry-detail-title">Inquiry Details</DialogTitle>
            <DialogDescription data-testid="admin-inquiry-detail-subtitle">
              Full inquiry information and quick contact actions.
            </DialogDescription>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-4" data-testid="admin-inquiry-detail-content">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="admin-inquiry-detail-grid">
                <div data-testid="admin-detail-patient-name">
                  <p className="text-xs uppercase text-slate-500">Patient</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedInquiry.full_name}</p>
                </div>
                <div data-testid="admin-detail-country">
                  <p className="text-xs uppercase text-slate-500">Country</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedInquiry.country}</p>
                </div>
                <div data-testid="admin-detail-treatment">
                  <p className="text-xs uppercase text-slate-500">Treatment</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedInquiry.treatment_type}</p>
                </div>
                <div data-testid="admin-detail-language">
                  <p className="text-xs uppercase text-slate-500">Language</p>
                  <p className="text-sm font-semibold text-slate-900">{selectedInquiry.language?.toUpperCase() || "EN"}</p>
                </div>
                <div data-testid="admin-detail-submitted-at">
                  <p className="text-xs uppercase text-slate-500">Submitted At</p>
                  <p className="text-sm font-semibold text-slate-900">{formatDateTime(selectedInquiry.submitted_at)}</p>
                </div>
                <div data-testid="admin-detail-status">
                  <p className="text-xs uppercase text-slate-500">Current Status</p>
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClassMap[selectedInquiry.status] || statusClassMap.new}`}
                    data-testid="admin-detail-status-badge"
                  >
                    {statusLabel(selectedInquiry.status)}
                  </span>
                </div>
              </div>

              <div data-testid="admin-detail-message-wrap">
                <p className="text-xs uppercase text-slate-500">Inquiry Message</p>
                <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-800" data-testid="admin-detail-message-text">
                  {selectedInquiry.message}
                </p>
              </div>

              <div className="flex flex-wrap gap-2" data-testid="admin-detail-contact-actions">
                <a
                  href={`https://wa.me/${selectedInquiry.phone_whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white"
                  data-testid="admin-detail-whatsapp-link"
                >
                  <Phone className="h-3.5 w-3.5" />
                  WhatsApp
                </a>
                <a
                  href={`tel:${selectedInquiry.phone_whatsapp}`}
                  className="inline-flex items-center gap-2 rounded-full bg-cyan-600 px-4 py-2 text-xs font-semibold text-white"
                  data-testid="admin-detail-call-link"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Call
                </a>
                <a
                  href={`mailto:${selectedInquiry.email}`}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                  data-testid="admin-detail-email-link"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;